import * as Sentry from '@sentry/react';
import { downloadAttachment } from 'api/v2/shared/downloadDocument';
import { uploadFile } from 'api/v2/upload';
import { getFileUrl } from 'api/v2/users';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Menu } from 'components/atoms/Menu';
import AIIcon from 'components/atoms/SVG/AIIcon';
import { Tooltip } from 'components/atoms/Tooltip';
import CustomLoader from 'components/molecules/CustomLoader.component';
import { pdfWorkerName } from 'config';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import useCustomDropzone from 'hooks/useCustomDropzone';
import useGetBlob from 'hooks/useGetPDFBlob';
import { get } from 'lodash';
import mime from 'mime/lite';
import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs, type PageProps } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-photo-view/dist/react-photo-view.css';
import { useMutation, useQuery } from 'react-query';
import type { ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import ResizeObserver from 'resizeObserver';
import type { IColors } from 'theme/colors';
import type { BoundingBoxOCR } from 'types/ai.types';
import { FilesUrls } from 'types/cacheKeys.types';
import type { TracingConfig } from 'utils/amplitude/amplitude.types';
import { globalyAcceptedFileUploaderTypes, maxFileSize } from 'utils/constants';
import {
  ChevronDownIconOutline,
  ChevronUpIconOutline,
  CopyOutlineIcon,
  DownloadIconOutline,
  DownloadOutlineIcon,
  EditOutlineIcon,
  RefreshIcon,
  Trash2OutlineIcon,
  Upload2OutlineIcon,
  ZoomInOutlineIcon,
  ZoomOutOutlineIcon,
} from 'utils/icons';
import CSVViewer from './CSVViewer';
import { renderLoader } from './utils';

// from the public folder
pdfjs.GlobalWorkerOptions.workerSrc = '/' + pdfWorkerName;

const options = {
  cMapUrl: `/cmaps/`,
};

const zoomContext = createContext({
  zoomIn: () => {},
  zoomOut: () => {},
  registerZoomIn: (f: () => void) => () => {},
  registerZoomOut: (f: () => void) => () => {},
});

type ZoomSubs = {
  onZoomIn: Record<number, () => void>;
  onZoomOut: Record<number, () => void>;
};

const ZoomProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const subs = useRef<ZoomSubs>({ onZoomIn: {}, onZoomOut: {} });

  const uid = useRef(0);

  const registerZoomIn = useCallback((f: () => void) => {
    const id = uid.current++;
    subs.current.onZoomIn[id] = f;
    return () => {
      delete subs.current.onZoomIn[id];
    };
  }, []);

  const registerZoomOut = useCallback((f: () => void) => {
    const id = uid.current++;
    subs.current.onZoomOut[id] = f;
    return () => {
      delete subs.current.onZoomOut[id];
    };
  }, []);

  const zoomIn = useCallback(() => {
    Object.values(subs.current.onZoomIn).forEach((f) => f());
  }, []);

  const zoomOut = useCallback(() => {
    Object.values(subs.current.onZoomOut).forEach((f) => f());
  }, []);

  const api = useMemo(
    () => ({
      zoomIn,
      zoomOut,
      registerZoomIn,
      registerZoomOut,
    }),
    [],
  );

  return <zoomContext.Provider value={api}>{children}</zoomContext.Provider>;
};

type FilePath = string | (() => Promise<string>);

type FileViewerFooterProps = {
  filePath: string;
  downloadOptions?: { label: string; path: FilePath }[];
  fileType: ReturnType<typeof getFileType>;
  onDelete?: () => void;
  onReplace?: (data: any) => void;
  onDownLoad?: () => void;
  onEdit?: (() => void) | null;
  onDuplicate?: (() => void) | null;
  disabled?: boolean;
  color?: IColors;
  tracing?: TracingConfig;
};

type FileViewerProps = Omit<FileViewerFooterProps, 'url' | 'fileType'> & {
  floatingItemsMargin: string;
  filePath: string;
  downloadOptions?: { label: string; url?: string }[];
  filePaths?: { label: string; url: string }[];
  boundingBox?: BoundingBoxOCR;
  hideControllers?:
    | boolean
    | ((fileType: ReturnType<typeof getFileType>) => boolean);
};

const filePathToFileName = (filePath: string) => {
  const parts = filePath.split('/');
  return parts[parts.length - 1];
};

const menuBtnClasses = classNames(
  'w-full text-init text-main text-sm py-2 px-4',
  'flex gap-2 items-center font-semibold',
  'hover:bg-primary-200 hover:bg-opacity-25 my-px',
);

const FileViewerFooter: FC<FileViewerFooterProps> = ({
  filePath,
  downloadOptions,
  onDelete,
  onReplace,
  onDownLoad,
  onEdit,
  onDuplicate,
  disabled,
  tracing,
  fileType,
}) => {
  const { t } = useTranslation();

  const { zoomIn, zoomOut } = useContext(zoomContext);

  const isPDF = fileType === 'pdf';
  const isImage = fileType === 'image';

  const validDownloadOptions = useMemo(() => {
    if (!downloadOptions) return [];
    return downloadOptions.filter(({ path }) => path);
  }, [downloadOptions]);

  const download = useMutation(
    (pathStringOrPromise: FilePath) => {
      if (!pathStringOrPromise) return Promise.reject('No path provided');
      const promiseOfPath =
        typeof pathStringOrPromise === 'string'
          ? Promise.resolve(pathStringOrPromise)
          : pathStringOrPromise();
      return promiseOfPath.then((filePath) =>
        getFileUrl(filePath).then(({ url }) =>
          downloadAttachment(url, filePathToFileName(filePath)),
        ),
      );
    },
    {
      onSuccess: () => {
        onDownLoad?.();
      },
    },
  );

  const upload = useMutation((file: File) => uploadFile({ file }), {
    onSuccess: (data) => {
      onReplace?.(data);
    },
  });

  const { getInputProps, open } = useCustomDropzone({
    onDropAccepted: ([file]) => upload.mutate(file),
    multiple: false,
    maxSize: maxFileSize,
    accept: globalyAcceptedFileUploaderTypes,
  });

  const { setIsHelperOpen, metadata } = useAiMetadata();

  return (
    <div className="grid auto-cols-fr grid-flow-col gap-6 bg-taxes-950 !text-white rounded-lg p-4">
      {metadata?.documentType && (
        <Tooltip content={t('ai_bot.tooltip.ask_ai')} placement="bottom">
          <Button
            onClick={() => setIsHelperOpen(true)}
            tracingEvent={tracing?.onZoomIn}
            structure="text"
            className="!text-white"
          >
            <div className="text-center flex flex-col gap-1.5">
              <AIIcon className="w-6 h-6 mx-auto stroke-white" />
              <span className="text-white">{t('ai_bot.ask_ai')}</span>
            </div>
          </Button>
        </Tooltip>
      )}

      {validDownloadOptions.length > 1 ? (
        <Menu
          position="top"
          buttonProps={{
            className: 'flex gap-1.5 justify-center text-sm',
            disabled: download.isLoading,
          }}
          contentWrapperClassName="!w-max bottom-full mb-2"
          trigger={(api) => (
            <>
              <div className="text-center items-center flex flex-col gap-1.5">
                {download.isLoading ? (
                  renderLoader('w-6')
                ) : (
                  <DownloadOutlineIcon className="w-6" />
                )}
                <div className="relative flex items-center gap-1">
                  <span className="text-white">{t('download')}</span>
                  <span className="absolute left-full top-1/2 -translate-y-1/2">
                    {api.open ? (
                      <ChevronUpIconOutline className="w-4 h-4" />
                    ) : (
                      <ChevronDownIconOutline className="w-4 h-4" />
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
        >
          <Menu.Group>
            {validDownloadOptions.map(({ label, path }, i) => (
              <Menu.Button
                key={i}
                className={menuBtnClasses}
                disabled={download.isLoading}
                onClick={() => download.mutate(path)}
              >
                <DownloadIconOutline className="w-5 h-5 text-primary-400" />
                <span className="leading-tight">{label}</span>
              </Menu.Button>
            ))}
          </Menu.Group>
        </Menu>
      ) : (
        <Button
          onClick={() => {
            const path = validDownloadOptions[0]?.path || filePath;
            download.mutate(path);
          }}
          tracingEvent={tracing?.onDownload}
          disabled={download.isLoading}
          structure="text"
          className="!text-white"
        >
          <div className="text-center flex flex-col gap-1.5">
            {download.isLoading ? (
              renderLoader('w-6 mx-auto')
            ) : (
              <DownloadOutlineIcon className="w-6 mx-auto" />
            )}
            <span className="text-white">{t('download')}</span>
          </div>
        </Button>
      )}
      {onEdit && (
        <Button
          onClick={() => onEdit()}
          tracingEvent={tracing?.onEdit}
          structure="text"
          className="!text-white"
        >
          <div className="text-center flex flex-col gap-1.5">
            <EditOutlineIcon className="w-6 mx-auto" />
            <span className="text-white">{t('edit')}</span>
          </div>
        </Button>
      )}
      {onDuplicate && (
        <Button
          onClick={() => onDuplicate()}
          tracingEvent={tracing?.onDuplicate}
          structure="text"
          className="!text-white"
        >
          <div className="text-center flex flex-col gap-1.5">
            <CopyOutlineIcon className="w-6 mx-auto" />
            <span className="text-white">{t('duplicate')}</span>
          </div>
        </Button>
      )}
      {/* {isPDF && (
        <Button
          disabled={!PDFBlob.data}
          onClick={() => {
            if (!PDFBlob.data) return;
            const fileURL = URL.createObjectURL(PDFBlob.data);
            window.open(fileURL, '_blank', 'noopener noreferrer');
          }}
          tracingEvent={tracing?.onOpenPDFInNewWindow}
          structure="text"
          className="!text-white"
        >
          <div className="text-center flex flex-col gap-1.5">
            <NewLinkOutlineIcon className="w-6 mx-auto" />
            <span className="text-white">{t('open')}</span>
          </div>
        </Button>
      )} */}
      {(isImage || isPDF) && (
        <>
          <Button
            onClick={() => {
              zoomIn();
            }}
            tracingEvent={tracing?.onZoomIn}
            structure="text"
            className="!text-white"
          >
            <div className="text-center flex flex-col gap-1.5">
              <ZoomInOutlineIcon className="w-6 mx-auto" />
              <span className="text-white">{t('zoom_in')}</span>
            </div>
          </Button>
          <Button
            onClick={() => {
              zoomOut();
            }}
            tracingEvent={tracing?.onZoomIn}
            structure="text"
            className="!text-white"
          >
            <div className="text-center flex flex-col gap-1.5">
              <ZoomOutOutlineIcon className="w-6 mx-auto" />
              <span className="text-white">{t('zoom_out')}</span>
            </div>
          </Button>
        </>
      )}
      <input {...getInputProps()} />
      {!disabled && (
        <>
          <Button
            onClick={() => open()}
            tracingEvent={tracing?.onReplace}
            disabled={upload.isLoading}
            structure="text"
            className="!text-white"
          >
            <div className="text-center flex flex-col gap-1.5">
              {upload.isLoading ? (
                renderLoader('w-6 mx-auto')
              ) : (
                <Upload2OutlineIcon className="w-6 mx-auto" />
              )}
              <span className="text-white">{t('replace')}</span>
            </div>
          </Button>
          <Button
            onClick={onDelete}
            tracingEvent={tracing?.onDelete}
            structure="text"
            className="!text-white"
          >
            <div className="text-center flex flex-col gap-1.5">
              <Trash2OutlineIcon className="w-6 mx-auto" />
              <span className="text-white">{t('delete')}</span>
            </div>
          </Button>
        </>
      )}
    </div>
  );
};

const BoundingBoxViewer: FC<{
  vertices: { x: number; y: number }[];
  pageWidth: number;
  pageHeight: number;
}> = ({ vertices, pageWidth, pageHeight }) => {
  const boundingBoxStyle = useMemo(() => {
    const [topLeft, topRight, bottomRight, bottomLeft] = vertices;
    const left = Math.min(topLeft.x, bottomLeft.x) * pageWidth;
    const top = Math.min(topLeft.y, topRight.y) * pageHeight;
    const right = Math.max(topRight.x, bottomRight.x) * pageWidth;
    const bottom = Math.max(bottomLeft.y, bottomRight.y) * pageHeight;

    const width = right - left;
    const height = bottom - top;

    return {
      left: left - 8,
      top: top - 4,
      width: width + 16,
      height: height + 8,
    };
  }, [vertices, pageWidth, pageHeight]);

  return (
    <div
      id="bounding-box"
      className="absolute pointer-events-none border-2 border-taxes bg-[rgba(128,89,200,0.1)] rounded-[4px]"
      style={boundingBoxStyle}
    />
  );
};

type PDFPageProps = {
  maxWidth: number;
  maxHeight: number;
  zoom: number;
  maxZoomLevel: number;
  boundingBox?: BoundingBoxOCR;
} & PageProps;

const PDFPage: FC<PDFPageProps> = ({
  maxWidth,
  maxHeight,
  zoom,
  maxZoomLevel,
  boundingBox,
  pageIndex = 0,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const [widthToWidthRatio, setWidthToWidthRatio] = useState<number>(0);
  const [widthToHeightRatio, setWidthToHeightRatio] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [pageHeight, setPageHeight] = useState<number>(0);

  const maxWidthBeforeZoom = maxWidth * 0.8;

  const width = Math.min(
    widthToWidthRatio * maxWidthBeforeZoom,
    widthToHeightRatio * maxHeight,
  );

  const zoomStep = (maxWidth - width) / maxZoomLevel;
  const showBoundingBox = boundingBox?.page_number === pageIndex + 1;

  const scrollIntoView = useCallback(
    (element: HTMLElement | null) => {
      if (!element || !showBoundingBox) return;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [showBoundingBox],
  );

  return (
    <div className="relative">
      <Page
        inputRef={scrollIntoView}
        pageIndex={pageIndex}
        onRenderSuccess={() => setLoaded(true)}
        {...props}
        width={width + zoom * zoomStep}
        canvasRef={(elm) => elm?.getContext?.('2d', { alpha: false })}
        className={classNames(props.className, { 'opacity-0': !loaded })}
        onLoadSuccess={(page) => {
          const viewportBasedOnWidth = page.getViewport({
            scale: maxWidthBeforeZoom / page.getViewport({ scale: 1 }).width,
          });
          const viewportBasedOnHeight = page.getViewport({
            scale: maxHeight / page.getViewport({ scale: 1 }).height,
          });
          setWidthToWidthRatio(viewportBasedOnWidth.width / maxWidthBeforeZoom);
          setWidthToHeightRatio(viewportBasedOnHeight.width / maxHeight);
          setPageWidth(page.width);
          setPageHeight(page.height);
        }}
      />
      {showBoundingBox && (
        <BoundingBoxViewer
          vertices={boundingBox.vertices}
          pageWidth={width + zoom * zoomStep}
          pageHeight={(width + zoom * zoomStep) * (pageHeight / pageWidth)}
        />
      )}
    </div>
  );
};

const PDFViewer: FC<{ url: string; boundingBox?: BoundingBoxOCR }> = ({
  url,
  boundingBox,
}) => {
  const blob = useGetBlob(url);

  const { registerZoomIn, registerZoomOut } = useContext(zoomContext);
  const docRef = useRef<HTMLDivElement>(null);
  const [pageMaxWidth, setPageMaxWidth] = useState<number>(0);
  const [pageMaxHeight, setPageMaxHeight] = useState<number>(0);

  const [zoomLevel, setZoomLevel] = useState(0);
  const [pages, setPages] = useState(1);

  const loaded = !!blob.data;

  const isError = blob.isError;

  const maxZoomLevel = 2;

  useEffect(() => {
    const zoomInUnsub = registerZoomIn(() => {
      setZoomLevel((s) => Math.min(s + 1, maxZoomLevel));
    });

    const zoomOutUnsub = registerZoomOut(() => {
      setZoomLevel((s) => Math.max(s - 1, 0));
    });

    return () => {
      zoomInUnsub();
      zoomOutUnsub();
    };
  }, [registerZoomIn, registerZoomOut]);

  useLayoutEffect(() => {
    if (!loaded || !docRef.current) return;
    const resizeObservable = new ResizeObserver((entries) => {
      setPageMaxWidth(entries[0].contentRect.width * 0.9);
      setPageMaxHeight(entries[0].contentRect.height);
    });
    resizeObservable.observe(docRef.current);
    return () => {
      resizeObservable.disconnect();
    };
  }, [loaded]);

  const retry = () => {
    blob.refetch();
  };

  return (
    <>
      {(!loaded || isError) && (
        <div className="h-full flex items-center justify-center">
          {isError ? <ErrorBtn onClick={retry} /> : <CustomLoader />}
        </div>
      )}
      {loaded && (
        <Document
          options={options}
          className={classNames('h-full flex flex-col items-center', {
            'opacity-0': !loaded,
          })}
          inputRef={docRef}
          file={blob.data}
          onLoadSuccess={(data) => {
            setPages(data.numPages);
          }}
        >
          {Array.from({ length: pages }).map((_, i) => (
            <PDFPage
              key={i}
              pageIndex={i}
              maxWidth={pageMaxWidth}
              maxHeight={pageMaxHeight}
              zoom={zoomLevel}
              maxZoomLevel={maxZoomLevel}
              className={classNames(i === pages - 1 ? 'mb-32' : 'mb-4')}
              boundingBox={boundingBox}
            />
          ))}
          <div className="opacity-0">-</div>
          {/* hack to see the margin on last page */}
        </Document>
      )}
    </>
  );
};

const ImageViewer: FC<{ url: string; boundingBox?: BoundingBoxOCR }> = ({
  url,
  boundingBox,
}) => {
  const { registerZoomIn, registerZoomOut } = useContext(zoomContext);

  const { isError, isLoading, data, refetch } = useQuery(
    url,
    (): Promise<{ src: string; width: number; height: number }> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onerror = () => reject();
        img.onload = () =>
          resolve({
            src: img.src,
            height: img.naturalHeight,
            width: img.naturalWidth,
          });
        img.src = url;
      }),
    {
      cacheTime: 0,
      retry: 1,
      onError: (error) => {
        Sentry.captureException(error);
      },
    },
  );

  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const [ref, setRef] = useState<ReactZoomPanPinchContentRef | null>(null);

  const { imageScale, imageHeight, imageWidth } = useMemo(() => {
    if (!containerWidth || !containerHeight || !data?.width || !data?.height) {
      return { imageScale: 0, imageHeight: 0, imageWidth: 0 };
    }

    // the image will be renderd with the width of the container anyway
    // so we try to get the height of the image if it was rendered with the width of the container

    const imageHeightToWidthRatio = data.height / data.width;

    const imageNewHeight = containerWidth * imageHeightToWidthRatio;

    return {
      imageScale: Math.min(1, containerHeight / imageNewHeight),
      imageHeight: Math.min(imageNewHeight, data.height),
      imageWidth: Math.min(containerWidth, data.width),
    };
  }, [containerWidth, containerHeight, data?.width, data?.height]);

  const handleResize = useCallback(() => {
    if (container !== null) {
      const rect = container.getBoundingClientRect();
      setContainerWidth(rect.width);
      setContainerHeight(rect.height);
    } else {
      setContainerWidth(0);
      setContainerHeight(0);
    }
  }, [container]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    if (!ref) return;
    const zoomInUnsub = registerZoomIn(() => {
      ref.zoomIn();
    });

    const zoomOutUnsub = registerZoomOut(() => {
      ref.zoomOut();
    });

    return () => {
      zoomInUnsub();
      zoomOutUnsub();
    };
  }, [registerZoomIn, registerZoomOut, ref]);

  if (isError) {
    return (
      <div className="h-full flex items-center justify-center">
        <ErrorBtn onClick={() => refetch()} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <CustomLoader />
      </div>
    );
  }

  return (
    <div
      className="flex h-full m-auto w-4/5 relative"
      ref={(el: HTMLDivElement | null) => setContainer(el)}
    >
      {imageScale > 0 && (
        <TransformWrapper
          key={`${containerWidth}x${containerHeight}`}
          ref={setRef}
          initialScale={imageScale}
          minScale={imageScale}
          centerOnInit
        >
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}
            contentClass="cursor-grab"
          >
            <img src={url} alt="" />
            {boundingBox && (
              <BoundingBoxViewer
                vertices={boundingBox.vertices}
                pageWidth={imageWidth}
                pageHeight={imageHeight}
              />
            )}
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  );
};

const getFileType = (url: string) => {
  const fileType = (mime as any).getType(url.split('?')[0]);

  if (!fileType) return null;
  if (fileType.includes('image/')) return 'image';
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType.includes('text/csv')) return 'csv';
  return null;
};

function renderFile(url: string, boundingBox?: BoundingBoxOCR): ReactNode {
  const fileType = getFileType(url);
  if (!fileType) return null;
  if (fileType === 'image')
    return <ImageViewer url={url} boundingBox={boundingBox} />;
  if (fileType === 'pdf')
    return <PDFViewer url={url} boundingBox={boundingBox} />;
  if (fileType === 'csv')
    return (
      <div className="overflow-x-auto px-10">
        <CSVViewer url={url} />
      </div>
    );
  return null;
}

type ErrorBtnProps = {
  onClick: () => void;
};
const ErrorBtn: FC<ErrorBtnProps> = (props) => {
  const { t } = useTranslation();
  return (
    <Button structure="text" {...props}>
      <div className="text-white text-center">
        <RefreshIcon className="w-20 mx-auto opacity-75" />
        <span className="text-lg font-semibold">
          {t('backend.error.something_went_wrong')}
          {'. '}
          <span className="border px-2 py-1 rounded">{t('retry')}</span>
        </span>
      </div>
    </Button>
  );
};

const FileViewer: FC<FileViewerProps> = ({
  filePath,
  hideControllers,
  floatingItemsMargin,
  boundingBox,
  ...restFooterProps
}) => {
  const { isLoading, isError, data, refetch } = useQuery(
    [FilesUrls.FileUrl, filePath],
    () => getFileUrl(filePath),
  );

  const content = useMemo(() => {
    if (!data?.url) return null;
    return renderFile(get(data, 'url', '') as string, boundingBox);
  }, [get(data, 'url'), boundingBox]);

  if (isLoading)
    return (
      <div className="h-full flex items-center justify-center">
        <CustomLoader />
      </div>
    );

  if (isError)
    return (
      <div className="h-full flex items-center justify-center">
        <ErrorBtn onClick={() => refetch()} />
      </div>
    );

  if (!data) return null;

  const fileType = getFileType(data.url);

  const shouldHideControllers =
    !content ||
    (typeof hideControllers === 'function'
      ? hideControllers(fileType)
      : !!hideControllers);

  return (
    <>
      <ZoomProvider>
        {content}
        {!shouldHideControllers && (
          <div
            style={{
              left: floatingItemsMargin
                ? `calc(50% - ${floatingItemsMargin} / 2)`
                : '50%',
            }}
            className={classNames(
              'fixed z-10 bottom-0 -translate-x-1/2 mb-8 hidden md:block',
            )}
          >
            <FileViewerFooter
              filePath={filePath}
              fileType={getFileType(data.url)}
              {...restFooterProps}
            />
          </div>
        )}
      </ZoomProvider>
    </>
  );
};

export default FileViewer;
